import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import LaunchIcon from "@mui/icons-material/Launch";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PolicyIcon from "@mui/icons-material/Policy";
import GavelIcon from "@mui/icons-material/Gavel";
import BusinessIcon from "@mui/icons-material/Business";
import LanguageIcon from "@mui/icons-material/Language";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SchoolIcon from "@mui/icons-material/School";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import { getImageUrl } from "../../../utils/helpers";

const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};

const AiCompliance = ({ baseConfig }) => {
  const { priColor, priHoverColor, tertColor, tertHoverColor } =
    baseConfig.siteBranding;

  // Key regulations affecting AI
  const regulations = [
    {
      name: "GDPR",
      region: "European Union",
      icon: <LanguageIcon />,
      description:
        "Data protection regulations with specific provisions relevant to automated decision-making",
    },
    {
      name: "CCPA/CPRA",
      region: "California, USA",
      icon: <AccountBalanceIcon />,
      description:
        "Consumer data privacy laws with provisions affecting AI systems using personal information",
    },
    {
      name: "FTC Guidelines",
      region: "United States",
      icon: <GavelIcon />,
      description:
        "Guidelines on unfair and deceptive practices applicable to AI technologies",
    },
    {
      name: "EU AI Act",
      region: "European Union",
      icon: <PolicyIcon />,
      description:
        "Comprehensive framework for regulating AI systems based on risk categories",
    },
    {
      name: "HIPAA",
      region: "United States",
      icon: <HealthAndSafetyIcon />,
      description:
        "Healthcare data regulations affecting AI applications in medical contexts",
    },
    {
      name: "Industry Standards",
      region: "Global",
      icon: <BusinessIcon />,
      description:
        "Sector-specific AI guidelines from industry organizations and standards bodies",
    },
  ];

  // Compliance services
  const complianceServices = [
    {
      title: "AI Regulatory Assessments",
      description:
        "Comprehensive evaluation of AI systems against applicable regulations",
    },
    {
      title: "Compliance Program Development",
      description: "Creation of tailored AI governance frameworks and policies",
    },
    {
      title: "Risk Mitigation Strategies",
      description:
        "Developing approaches to address identified compliance risks",
    },
    {
      title: "Documentation & Recordkeeping",
      description: "Establishing processes to document compliance efforts",
    },
    {
      title: "Training & Education",
      description: "Educational programs on AI compliance requirements",
    },
    {
      title: "Ongoing Compliance Monitoring",
      description:
        "Continuous assessment of AI systems against evolving regulations",
    },
  ];

  return (
    <Box sx={{ mb: 6 }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          backgroundColor: `${tertColor}22`,
          borderRadius: "4px 4px 0 0",
        }}
      >
        <SecurityIcon sx={{ mr: 2, color: tertColor }} />
        <Typography variant="h5" fontWeight="bold">
          AI Regulatory & Industry Compliance
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${tertColor}33`,
          borderTop: 0,
          borderRadius: "0 0 4px 4px",
          mb: 4,
        }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h6"
              paragraph
              sx={{ mb: 4 }}
              fontWeight="bold"
              color={tertColor}
            >
              Navigating the Complex AI Regulatory Landscape
            </Typography>
            <Typography variant="body1" paragraph>
              Kama Thuo, PLLC assists clients in creating robust AI governance
              frameworks to ensure regulatory compliance and adherence to
              industry best practices. Our AI regulatory compliance services
              cover relevant global regulations (GDPR, CCPA, FTC guidelines) and
              industry standards specific to AI and data governance.
            </Typography>

            <Typography variant="body1" paragraph>
              The regulatory landscape for artificial intelligence is rapidly
              evolving, with new requirements emerging at international,
              national, and state levels. Our team stays at the forefront of
              these developments, providing clients with timely guidance on
              compliance obligations and practical implementation strategies.
            </Typography>

            <Typography variant="body1" paragraph sx={{ mb: 4 }}>
              We offer tailored compliance assessments, strategic policy
              formulation, and practical recommendations for operationalizing AI
              governance frameworks. Identify and address compliance risks
              specific to your AI applications using our intuitive and detailed
              AI compliance checker tool.
            </Typography>

            <Box sx={{ mt: 3 }}>
              <Button
                startIcon={<LaunchIcon />}
                variant="contained"
                sx={{
                  mr: 2,
                  backgroundColor: tertColor,
                  color: "white",
                  "&:hover": {
                    color: "white",
                    backgroundColor: tertHoverColor,
                  },
                }}
                component={RouterLink}
                to="/legal-compliance-for-ai"
              >
                AI Legal Compliance
              </Button>
              <Button
                startIcon={<LaunchIcon />}
                variant="contained"
                sx={{
                  backgroundColor: priColor,
                  color: "white",
                  "&:hover": { color: "white", backgroundColor: priHoverColor },
                }}
                component={RouterLink}
                to="/compliance-checker-for-ai"
              >
                Try Compliance Checker
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              elevation={3}
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                height: "100%",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                },
              }}
            >
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${tertColor} 0%, ${tertColor}CC 100%)`,
                  p: 2.5,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color="white"
                  align="center"
                >
                  Key Compliance Focus Areas
                </Typography>
                <Box
                  sx={{
                    position: "absolute",
                    right: -20,
                    top: -20,
                    width: 100,
                    height: 100,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255,255,255,0.1)",
                    zIndex: 0,
                  }}
                />
              </Box>

              <CardContent>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Data Privacy & Protection"
                      secondary="Ensuring AI systems handle personal data in compliance with privacy regulations"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Transparency & Explainability"
                      secondary="Meeting requirements for AI system transparency and explainable outcomes"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Risk Assessment & Management"
                      secondary="Implementing frameworks to assess and mitigate AI-related risks"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Bias & Fairness"
                      secondary="Addressing requirements related to non-discrimination and algorithmic fairness"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Human Oversight"
                      secondary="Ensuring appropriate human monitoring and intervention capabilities"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Documentation & Recordkeeping"
                      secondary="Maintaining required documentation of AI development and operation"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Box>
          <Typography
            variant="h6"
            gutterBottom
            fontWeight="bold"
            color={tertColor}
          >
            Key Regulations Affecting AI Systems
          </Typography>

          <Grid container spacing={3}>
            {regulations.map((regulation, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  elevation={1}
                  sx={{
                    height: "100%",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Box sx={{ color: tertColor, mr: 1.5 }}>
                        {regulation.icon}
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {regulation.name}
                        </Typography>
                        <Chip
                          label={regulation.region}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body2">
                      {regulation.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box>
          <Typography
            variant="h6"
            gutterBottom
            fontWeight="bold"
            color={tertColor}
          >
            Our AI Compliance Services
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Typography variant="body1" paragraph sx={{ mb: 6 }}>
                Our firm offers comprehensive AI compliance services to help
                your organization navigate the complex and evolving regulatory
                landscape. We take a practical, risk-based approach that
                balances compliance obligations with operational needs, helping
                you implement effective governance without unnecessarily
                hindering innovation.
              </Typography>

              <Grid container spacing={2}>
                {complianceServices.map((service, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ display: "flex", mb: 2 }}>
                      <ArrowRightIcon
                        sx={{ color: tertColor, mr: 1.5, mt: 0.5 }}
                      />
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {service.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {service.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  backgroundColor: "#f9f9f9",
                  borderRadius: 2,
                  border: `1px solid ${tertColor}33`,
                }}
              >
                <Typography
                  variant="h6"
                  // gutterBottom
                  fontWeight="bold"
                  color={tertColor}
                >
                  Benefits of Proactive AI Compliance
                </Typography>

                <List>
                  <ListItem>
                    <ListItemIcon>
                      <VerifiedUserIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Risk Mitigation"
                      secondary="Reduce legal, regulatory, and reputational risks associated with AI deployment"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <BusinessIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Market Advantage"
                      secondary="Differentiate your AI products and services through demonstrable compliance"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <SchoolIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Future-Proofing"
                      secondary="Build systems that can adapt to evolving regulatory requirements"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <PolicyIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Stakeholder Trust"
                      secondary="Build confidence with users, customers, partners, and investors"
                    />
                  </ListItem>
                </List>

                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <Button
                    startIcon={<PhoneInTalkIcon />}
                    variant="contained"
                    onClick={() => scrollToSection("contact-section")}
                    sx={{
                      backgroundColor: tertColor,
                      color: "white",
                      "&:hover": {
                        color: "white",
                        backgroundColor: tertHoverColor,
                      },
                    }}
                  >
                    Schedule a Consultation
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box
                component="img"
                src={getImageUrl("compliance_checker_banner.png")}
                alt="AI Compliance Checker Tool"
                sx={{
                  width: "100%",
                  height: "auto",
                  borderRadius: 2,
                  // boxShadow: "0 4px 2px rgba(0,0,0,0.1)",
                }}
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <Typography
                variant="h6"
                gutterBottom
                fontWeight="bold"
                color={tertColor}
              >
                Our AI Legal Compliance Checker Tool
              </Typography>

              <Typography variant="body1" paragraph>
                Identify potential compliance issues in your AI systems with our
                AI Compliance Checker tool. This interactive assessment helps
                you evaluate your AI applications against key regulatory
                requirements and industry best practices, providing practical
                recommendations for addressing identified gaps.
              </Typography>

              <Typography variant="body1" paragraph>
                The tool covers various compliance dimensions, including data
                privacy, algorithmic fairness, transparency, security, and
                governance. After completing the assessment, you'll receive a
                detailed report highlighting strengths, weaknesses, and specific
                action items to enhance your AI compliance posture.
              </Typography>

              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<LaunchIcon />}
                  component={RouterLink}
                  to="/compliance-checker-for-ai"
                  sx={{
                    backgroundColor: tertColor,
                    color: "white",
                    "&:hover": {
                      color: "white",
                      backgroundColor: tertHoverColor,
                    },
                  }}
                >
                  Try Our AI Compliance Checker
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default AiCompliance;
